import type { FilterItem, Rep, RepStatusEnum } from "@repo/models";
import type { GridList, GridPagination } from "@repo/ui";
import type { AxiosRequestConfig } from "axios";

import { RepDeactivationTypes } from "@repo/models";

import type { RepSettings } from "../models/repSettings";

import { BaseController } from "./baseController";
import { BaseGridApi } from "./baseGridApi";

export interface RepsFilter extends GridPagination {
	// segment filters (multi)
	brands?: (number | string)[];
	channels?: (number | string)[];
	marketers?: (number | string)[];
	states?: (number | string)[];
	branches?: (number | string)[];

	position?: (string | number)[];

	// status filters
	activeStatus?: (string | number)[]; // Inactive=4, Active=1, Onboarding=2, Awaiting Activation=3, On Hold=5
	status?: RepStatusEnum[];
	disciplineStatus?: (string | number)[];

	// misc filters
	lastFourSSN?: string;
	dateOfBirth?: Date | null;
	onboardingStartDate?: Date | null;
	onboardingEndDate?: Date | null;
	isTest?: boolean | null;
	agentId?: string;

	// text filters
	phoneNumber: string;
	email: string;
	firstName: string;
	lastName: string;
	login: string;

	segments: boolean;
	isSlim?: boolean;
}

export const controller = "RepresentativesTest";

export default class RepsApi {
	static Get = BaseController.GetFn(controller);
	static Post = BaseController.PostFn(controller);

	/** Loads all reps matching filters */
	static getReps(filter: RepsFilter, config?: AxiosRequestConfig) {
		return RepsApi.Get<GridList<Rep>>("GetRepresentatives", filter, config);
	}

	static getSettings() {
		return RepsApi.Get<RepSettings>("GetSettings");
	}

	static getRepTypes() {
		return RepsApi.Get<FilterItem[]>("GetRepTypes");
	}

	static validateActivation(repSegmentId: number) {
		return RepsApi.Get<string>("CanActivate", { repSegmentId });
	}

	static forceActivate(repSegmentId: number) {
		return RepsApi.Post("ForceActivate", { repSegmentId });
	}

	static deactivate(repSegmentId: number) {
		const details = {
			deactivationReason: "Deactivated from Grid",
			deactivationTypeId: RepDeactivationTypes.Deactivated,
		};
		return RepsApi.Post("Deactivate", { repSegmentId }, details);
	}

	static deactivateAll(repId: number) {
		const details = {
			deactivationReason: "Deactivated from Grid",
			deactivationTypeId: RepDeactivationTypes.Deactivated,
		};
		return RepsApi.Post("DeactivateAll", { repId }, details);
	}

	/** Loads saved grid settings for representatives */
	static loadGridSettings() {
		return BaseGridApi.loadGridSettings(controller, "Representatives");
	}

	/** Loads saved grid settings for representatives. Pass empty string to clear. */
	static saveGridSettings(settingsJson: string) {
		return BaseGridApi.saveGridSettings(controller, "Representatives", settingsJson);
	}

	static downloadBulkUploadTemplate() {
		return RepsApi.Get<Blob>("DownloadBulkUploadTemplate", {}, { responseType: "blob" });
	}

	static processBulkImportFile(data: FormData) {
		return RepsApi.Post<string[]>("ProcessBulkImportFile", {}, data);
	}

	static getGridColumns() {
		return BaseGridApi.getGridColumns<any>(controller, "Representatives");
	}
}
